import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import UserTable from '../../components/admin/UserTable.jsx';
import UserDetailsPanel from '../../components/admin/UserDetailsPanel.jsx';
import FilterModule from '../../modules/filter/FilterModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';
import UserCreationModal from '../../components/admin/UserCreationModal.jsx';
import UserDeactivationModal from '../../components/admin/UserDeactivationModal.jsx';
import UserReactivationModal from '../../components/admin/UserReactivationModal.jsx';
import UserBillingPanel from '../../components/admin/UserBillingPanel.jsx';
import UserActivityDashboard from '../../components/admin/UserActivityDashboard.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Main user management component for admin interface
 */
const UserManagement = () => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    subscription: 'all',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showBillingPanel, setShowBillingPanel] = useState(false);
  const [showActivityDashboard, setShowActivityDashboard] = useState(false);
  const [userForAction, setUserForAction] = useState(null);

  /**
   * Fetch users from API with current filters and pagination
   */
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      // Add optional filters
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.subscription !== 'all') params.append('subscription', filters.subscription);

      const response = await apiRequest(`/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setUsers(data.users || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0
      }));

    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Failed to load users');
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, filters, pagination.page, pagination.limit, showToast]);

  /**
   * Handle filter changes from FilterModule
   */
  const handleFiltersChange = useCallback((activeFilters) => {
    // Convert FilterModule activeFilters to legacy filter format
    const newFilters = {
      search: '',
      status: 'all',
      subscription: 'all',
      sortBy: 'created_at',
      sortOrder: 'desc'
    };

    activeFilters.forEach(filter => {
      switch (filter.id) {
        case 'name':
        case 'email':
          // Use the first text filter as search
          if (!newFilters.search) {
            newFilters.search = filter.value;
          }
          break;
        case 'status':
          newFilters.status = filter.value;
          break;
        case 'subscription':
          newFilters.subscription = filter.value;
          break;
        default:
          break;
      }
    });

    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle legacy filter changes (for backward compatibility)
   */
  const handleLegacyFiltersChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle pagination changes
   */
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  /**
   * Handle user selection
   */
  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
  }, []);

  /**
   * Handle user updates
   */
  const handleUserUpdate = useCallback((updatedUser) => {
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    setSelectedUser(updatedUser);
    showToast('User updated successfully', 'success');
  }, [showToast]);

  /**
   * Handle user creation
   */
  const handleUserCreated = useCallback((newUser) => {
    setUsers(prev => [newUser, ...prev]);
    setPagination(prev => ({ ...prev, total: prev.total + 1 }));
  }, []);

  /**
   * Handle user deactivation
   */
  const handleUserDeactivated = useCallback((updatedUser) => {
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    if (selectedUser?.id === updatedUser.id) {
      setSelectedUser(updatedUser);
    }
  }, [selectedUser]);

  /**
   * Handle user reactivation
   */
  const handleUserReactivated = useCallback((updatedUser) => {
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    if (selectedUser?.id === updatedUser.id) {
      setSelectedUser(updatedUser);
    }
  }, [selectedUser]);

  /**
   * Open deactivation modal
   */
  const openDeactivateModal = useCallback((user) => {
    setUserForAction(user);
    setShowDeactivateModal(true);
  }, []);

  /**
   * Open reactivation modal
   */
  const openReactivateModal = useCallback((user) => {
    setUserForAction(user);
    setShowReactivateModal(true);
  }, []);

  /**
   * Open billing panel
   */
  const openBillingPanel = useCallback((user) => {
    setUserForAction(user);
    setShowBillingPanel(true);
  }, []);

  /**
   * Open activity dashboard
   */
  const openActivityDashboard = useCallback((user) => {
    setUserForAction(user);
    setShowActivityDashboard(true);
  }, []);

  // Load users on component mount and when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            Manage user accounts, subscriptions, and access
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 dark:text-white"
        >
          <span>+</span>
          <span>Create User</span>
        </button>
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {pagination.total > 0 && (
            <span>
              Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </span>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`${theme === 'dark' ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex items-center">
            <span className={`${theme === 'dark' ? 'text-red-400' : 'text-red-600'} mr-2`}>⚠️</span>
            <span className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>{error}</span>
            <button
              onClick={fetchUsers}
              className={`ml-auto ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'} underline`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters sidebar */}
        <div className="lg:col-span-1">
          <ModuleContextProvider
            user={{ id: 'admin', permissions: ['admin'] }}
            permissions={[{ id: 'admin', name: 'Admin Access' }]}
            apiClient={{ get: () => {}, post: () => {}, put: () => {}, delete: () => {} }}
            theme={{ mode: theme }}
            toast={{ success: showToast, error: showToast, warning: showToast, info: showToast }}
          >
            <FilterModule
              config={{
                context: 'admin-users',
                filters: [], // Will use context-specific defaults
                layout: 'vertical',
                persistFilters: true,
                storageKey: 'admin-users-filters',
                showClearAll: true,
                realTimeFiltering: true,
                debounceMs: 500,
                onFilterChange: handleFiltersChange,
                onFilterReset: () => handleLegacyFiltersChange({
                  search: '',
                  status: 'all',
                  subscription: 'all',
                  sortBy: 'created_at',
                  sortOrder: 'desc'
                })
              }}
              className="h-fit"
              testId="admin-users-filter"
            />
          </ModuleContextProvider>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Users table */}
          <UserTable
            users={users}
            loading={loading}
            selectedUser={selectedUser}
            onUserSelect={handleUserSelect}
            pagination={pagination}
            onPageChange={handlePageChange}
            onSort={(sortBy, sortOrder) => handleLegacyFiltersChange({ sortBy, sortOrder })}
          />

          {/* User details panel */}
          {selectedUser && (
            <UserDetailsPanel
              user={selectedUser}
              onUpdate={handleUserUpdate}
              onDeactivate={openDeactivateModal}
              onReactivate={openReactivateModal}
              onViewBilling={openBillingPanel}
              onViewActivity={openActivityDashboard}
              onClose={() => setSelectedUser(null)}
            />
          )}
        </div>
      </div>

      {/* Modals and Panels */}
      <UserCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
      />

      <UserDeactivationModal
        isOpen={showDeactivateModal}
        user={userForAction}
        onClose={() => {
          setShowDeactivateModal(false);
          setUserForAction(null);
        }}
        onDeactivated={handleUserDeactivated}
      />

      <UserReactivationModal
        isOpen={showReactivateModal}
        user={userForAction}
        onClose={() => {
          setShowReactivateModal(false);
          setUserForAction(null);
        }}
        onReactivated={handleUserReactivated}
      />

      {showBillingPanel && userForAction && (
        <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-900'} bg-opacity-50 flex items-center justify-center z-50 p-4`}>
          <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <UserBillingPanel
              userId={userForAction.id}
              onClose={() => {
                setShowBillingPanel(false);
                setUserForAction(null);
              }}
            />
          </div>
        </div>
      )}

      {showActivityDashboard && userForAction && (
        <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-900'} bg-opacity-50 flex items-center justify-center z-50 p-4`}>
          <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <UserActivityDashboard
              userId={userForAction.id}
              onClose={() => {
                setShowActivityDashboard(false);
                setUserForAction(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
